import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {
  setSearchQuery,
  setSearchResults,
  selectSearchLoading,
  SearchResultsExtension,
  setSearchFilters,
} from 'app/store/search/searchSlice';
import {useSearchByKeywordQuery} from 'app/store/search/searchApi';
import store from 'app/store';
import SearchInput from './SearchInput';
//import SearchFilters from './SearchFilters';
import SearchResultsView from './SearchResultsView';
import {Box, CircularProgress, Paper, Stack, useTheme} from '@mui/material';
import {useMatomo} from '@jonkoops/matomo-tracker-react';
import {selectUserProfile} from 'app/store/auth/authSlice';
import {DocumentCategories, ResourceTabs} from 'app/types/Shared';
import DocumentFilters from 'components/DocumentFilters/DocumentFilters';
import {selectMasterSOP} from 'app/store/artifacts/artifactSlice';

interface SearchUIProps {
  initialTabIndex?: number;
  initialQuery?: string;
  initialFilters?: Array<DocumentCategories>;
  storybook?: boolean;
  veiwAllCallback?: () => Promise<void>;
}

const SearchUI: React.FC<SearchUIProps> = ({
  initialQuery = '',
  initialFilters = [],
  storybook = false,
  initialTabIndex = 0,
  veiwAllCallback,
}) => {
  const theme = useTheme();
  const authToken = useSelector(selectUserProfile).token;
  const [query, setQuery] = useState(initialQuery);
  const [activeFilters, setActiveFilter] = useState(initialFilters);
  const [currentTab, setCurrentTab] = React.useState(Object.values(ResourceTabs)[initialTabIndex]);
  const [currentTabIndex, setCurrentTabIndex] = React.useState(initialTabIndex);
  const {data, isSuccess} = useSearchByKeywordQuery(query, {skip: !authToken});
  const resultsExtension = new SearchResultsExtension(data ? data : {processes: [], artifacts: []});
  const masterSOP = useSelector(selectMasterSOP);
  const loading = useSelector(selectSearchLoading);
  const {trackEvent} = useMatomo();

  useEffect(() => {
    store.dispatch(setSearchFilters(activeFilters));
    store.dispatch(setSearchQuery(query));
  }, [query, activeFilters]);

  const handleSearch = (query: string) => {
    trackEvent({category: 'Search', action: 'Search Keyword', name: query});

    // clear out the results
    store.dispatch(setSearchResults({processes: [], artifacts: []}));
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    params.set('q', query);
    //add tab index to persist tab state while on the search page
    params.set('tab', currentTabIndex.toString());
    url.search = params.toString();
    window.location.href = url.toString();
    setQuery(query);
  };

  const handleSelectFilter = (filter: Array<DocumentCategories>) => {
    trackEvent({category: 'Search', action: 'Search Results Filtered', name: filter.join(', ')});

    setActiveFilter(filter);
  };

  const handleTabChange = (tab: ResourceTabs, tabIndex: number) => {
    setActiveFilter([]);
    setCurrentTab(tab);
    setCurrentTabIndex(tabIndex);
  };

  //Disable any filters that don't have any results
  const getAvalibleFilters = () => {
    return Object.values(DocumentCategories).filter((filter) => {
      if (filter === DocumentCategories.Processes) {
        return resultsExtension.getProcesses(currentTab).length > 0;
      } else {
        return resultsExtension.getProcessDocuments([filter], currentTab).some((items) => items?.artifacts?.length > 0);
      }
    });
  };

  //disable tabs that don't have any results
  const tabsHaveResults = () => {
    const tabs = Object.values(ResourceTabs).filter((tab) => {
      return resultsExtension.getProcessDocuments([], tab).length > 0;
    });
    //handle tab change if not results in the current tab
    if (tabs?.length === 0 || tabs.includes(currentTab)) {
      return tabs;
    } else {
      //find index of current tab
      const index = Object.values(ResourceTabs).indexOf(currentTab);
      const tab = tabs.find((tab) => tab === currentTab);
      if (tab) {
        handleTabChange(tab, index);
      } else {
        //defualt to first tab
        const tabidx = Object.values(ResourceTabs).indexOf(tabs[0]);
        handleTabChange(tabs[0], tabidx);
      }
      return tabs;
    }
  };

  return (
    <Box>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: '24px', pt: 1}}>
        <Stack direction={'row'} spacing={2} px={6}>
          <SearchInput onSearch={handleSearch} initialQuery={query} />
          {loading && (
            <Box sx={{pt: 8}}>
              <CircularProgress size={32} />
            </Box>
          )}
        </Stack>
        <DocumentFilters
          filters={getAvalibleFilters()}
          activeFilters={activeFilters}
          currentTabIndex={currentTabIndex}
          onTabChange={handleTabChange}
          veiwAllCallback={veiwAllCallback}
          onFilter={handleSelectFilter}
          tabs={tabsHaveResults()}
        />
      </Box>
      <Paper
        sx={{
          minHeight: '55vh',
          background: theme.palette.grey[100],
          borderTop: `1px solid ${theme.palette.grey[500]}`,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
        }}
      >
        {!loading && authToken && isSuccess && (
          <SearchResultsView
            activeFilters={activeFilters}
            results={resultsExtension}
            storybook={storybook}
            currentTab={currentTab}
            masterSOP={masterSOP}
          />
        )}
      </Paper>
    </Box>
  );
};

export default SearchUI;
